import { NewSourceModalComponent } from '../../gingr-new-items-modals/new-source-modal/new-source-modal.component';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { MatButton,MatIconButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { SortDirection } from '@angular/material/sort';
import { MatTooltip } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { INITIAL_PAGING, TPaging } from '@core/interfaces/TPaging';
import { Subscription } from 'rxjs';

import { TFolderItem } from '@core/interfaces/TFolderItem';
import { TSourceItem } from '@core/interfaces/TSourceItem';
import { SourceSortField } from '@core/interfaces/TSources';

import { JobStatusComponent } from '@shared/components/job-status/job-status.component';
import { CommonService } from '@shared/services/common.service';
import { FoldersService } from '@shared/services/folders.service';

import { FolderSourcesListComponent } from '../../folder-sources-list/folder-sources-list.component';
import { EditSourceFolderModalComponent } from '../../modals/edit-source-folder-modal/edit-source-folder-modal.component';

@Component({
    selector: 'app-folders-content',
    templateUrl: './folders-content.component.html',
    styleUrls: ['./folders-content.component.scss'],
    standalone: true,
    imports: [
      MatIconButton,
      MatTooltip,
      MatIcon,
      MatButton,
      MatProgressSpinner,
      FolderSourcesListComponent,
      JobStatusComponent,
    ]
})
export class FoldersContentComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public selectedItem: TFolderItem = {} as TFolderItem;
  @Input() public returnedFromSource = false;
  private _subs: Subscription[] = [];
  folderSources: TSourceItem[] = [];
  isLoading = false;
  isApprovingFolder = false;
  paging: TPaging = {} as TPaging;
  sorting: { sortField: SourceSortField; sortDirection: SortDirection } = {
    sortField: 'name',
    sortDirection: 'asc',
  };

  @Output() public sourceSelected: EventEmitter<TSourceItem> = new EventEmitter<TSourceItem>();

  constructor(
    private _commonService: CommonService,
    private _folderService: FoldersService,
    public dialog: MatDialog,
    private router: Router
  ) {}

  ngOnInit(): void {
    this._subs.push(
      this._folderService.getFoldersObservable().subscribe(res => {
        const newFolderData: TFolderItem | undefined = res.find(
          it => it._id === this.selectedItem._id
        );
        if (newFolderData) {
          this.selectedItem = newFolderData;
        }
      })
    );
    this._subs.push(
      this._commonService.getIsLoadingObservable().subscribe(res => {
        this.isLoading = res;
      })
    );
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.selectedItem?.previousValue !== changes.selectedItem?.currentValue) {
      if (!this.returnedFromSource) {
        this._folderService.setFolderSourcesPaging(INITIAL_PAGING);
      }

      this._subs.push(
        this._folderService.getFolderSourcesPagingObservable().subscribe(res => {
          this.paging = res;
        })
      );
      this.getFolderSources();
      this._subs.push(
        this._folderService.getFolderSourcesObservable().subscribe(res => {
          this.folderSources = res;
        })
      );
    }
  }

  public ngOnDestroy(): void {
    this._subs.forEach(sub => sub.unsubscribe());
  }

  handleSourceSelected(source: TSourceItem): void {
    this.sourceSelected.emit(source);
  }

  public openEditModal(): void {
    this.dialog.open(EditSourceFolderModalComponent, {
      data: { source: this.selectedItem },
      width: '80vw',
      height: '100vh',
      maxWidth: '80vw',
      maxHeight: '100vh',
      position: { right: '0' },
      panelClass: 'edit-source-folder-modal',
      hasBackdrop: true,
      disableClose: false,
    });
  }

  handleSortChange(sortState: { sortField: SourceSortField; sortDirection: SortDirection }): void {
    this.sorting = sortState;
    this.getFolderSources();
  }

  handlePageChange(pagingState: TPaging): void {
    this.paging = pagingState;
    this.getFolderSources();
  }

  public openNewSourceModal(): void {
    this.dialog.open(NewSourceModalComponent, {
      data: { folderId: this.selectedItem._id },
      width: '80vw',
      height: '100vh',
      maxWidth: '80vw',
      maxHeight: '100vh',
      position: { right: '0' },
      panelClass: 'new-source-modal',
      hasBackdrop: true,
      disableClose: false,
    });
  }

  public goToBatchUpload(): void {
    this.router.navigate(['/sources/batch-upload/', { folderId: this.selectedItem._id }]);
  }

  public toggleApproval(): void {
    if (!this.selectedItem._id) return;

    this.isApprovingFolder = true;
    const newApprovalState = !this.selectedItem.approved;

    this._folderService.approveFolder(this.selectedItem._id, newApprovalState, () => {
      this.isApprovingFolder = false;
      const message = newApprovalState ? 'Folder approved' : 'Folder approval removed';
      this._commonService.openSuccessSnackBar(message);
    });
  }

  private getFolderSources(): void {
    this._folderService.getFolderSources(
      this.selectedItem._id,
      this.paging.page,
      this.paging.limit,
      this.sorting.sortDirection,
      this.sorting.sortField
    );
  }
}
